import { useTranslation } from 'react-i18next';
import { useCanvaImportStore } from '@/stores/canvaImportStore';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';

/**
 * CanvaImportMinimized Component
 *
 * Shows a minimized progress indicator in the bottom-left corner
 * when a Canva import is running in the background.
 * Click to reopen the modal when processing.
 */
export const CanvaImportMinimized = () => {
  const { t } = useTranslation('common');
  const { activeImport, clearBackgroundImport, reopenModal } = useCanvaImportStore();

  if (!activeImport) {
    return null;
  }

  const { status } = activeImport;
  const isProcessing = !status || status.status === 'PROCESSING' || status.status === 'PENDING';
  const isComplete = status?.status === 'COMPLETED';
  const isFailed = status?.status === 'FAILED' || status?.status === 'CANCELLED';

  const progress = status
    ? Math.round((status.processedCount / status.totalDesigns) * 100)
    : 0;

  const handleClick = () => {
    if (isProcessing) {
      reopenModal();
    }
  };

  return (
    <div className="fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+12px+var(--floating-stack-offset))] lg:bottom-4 left-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div
        onClick={handleClick}
        className={`
          flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border
          ${isComplete ? 'bg-green-50 border-green-200' : ''}
          ${isFailed ? 'bg-red-50 border-red-200' : ''}
          ${isProcessing ? 'bg-white border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors' : ''}
        `}
        role={isProcessing ? 'button' : undefined}
        tabIndex={isProcessing ? 0 : undefined}
        onKeyDown={isProcessing ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        } : undefined}
        aria-label={isProcessing ? t('canva_reopen_import_modal') : undefined}
      >
        {/* Icon */}
        <div className="flex-shrink-0">
          {isProcessing && (
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          )}
          {isComplete && (
            <Icon name={ICONS.CHECK} size="sm" color="text-green-600" />
          )}
          {isFailed && (
            <Icon name={ICONS.CLOSE} size="sm" color="text-red-600" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900">
            {isProcessing && t('canva_importing')}
            {isComplete && t('canva_import_complete')}
            {isFailed && (status?.status === 'CANCELLED' ? t('canva_import_cancelled') : t('canva_import_failed'))}
          </div>

          {status && (
            <div className="text-xs text-gray-500">
              {status.processedCount}/{status.totalDesigns} {t('designs')}
              {isProcessing && ` (${progress}%)`}
            </div>
          )}
        </div>

        {/* Progress bar (only when processing) */}
        {isProcessing && status && (
          <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* Dismiss button (only when complete/failed) */}
        {!isProcessing && (
          <button
            onClick={clearBackgroundImport}
            className="flex-shrink-0 p-1 hover:bg-gray-100 rounded"
            aria-label={t('dismiss')}
          >
            <Icon name={ICONS.CLOSE} size="xs" color="text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );
};
